/**
 * Admin · Hosting / CDN settings (FR-008) — SUPER-ADMIN ONLY. Configures the
 * hosting backend (local vs R2), public host URL, and Cloudflare R2 credentials.
 * The server never echoes secrets back — only `r2_configured` and the non-secret
 * account/bucket identifiers.
 */
import { useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import { ActionBar } from "../ui/ActionBar.js";
import { EmptyState } from "../ui/EmptyState.js";
import { useSessionStore } from "../stores/session.js";
import { cacheEpoch, readCacheEntry, writeCache } from "../stores/cache.js";
import { StaleNotice } from "./StaleNotice.js";
import type { Staleness } from "../stores/useCachedResource.js";
import type { HostingSettings, HostingSettingsInput } from "@genz/platform";

/** Cache key — seeds the form instantly on tab re-open (cache-first; the response
 *  never contains secrets, so this is safe to persist). */
const HOSTING_CACHE = "admin.hosting";

/**
 * Fresh-install defaults. The cloud stores hosting config as a JSON blob and
 * serializes it as-is, so on a brand-new deployment `hosting_settings` is `{}` and
 * the response carries only the `*_configured` flags — every scalar comes back
 * `undefined`. Feeding that straight into `useState` flipped the inputs from
 * controlled to uncontrolled (React warnings) AND made Save omit those keys, so
 * the "local" backend the operator could plainly see was never actually persisted.
 * Coalesce here, in ONE place, so the form always holds a real value.
 */
const HOSTING_DEFAULTS = {
  hosting_enabled: false,
  hosting_backend: "local",
  hosting_public_host: "",
  r2_account_id: "",
  r2_bucket: "",
} as const;

export function HostingTab() {
  const { client } = useServices();
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const cachedSettings = readCacheEntry<HostingSettings>(HOSTING_CACHE);
  const [settings, setSettings] = useState<HostingSettings | null>(cachedSettings?.data ?? null);
  const [loading, setLoading] = useState(cachedSettings == null);
  const [error, setError] = useState<string | null>(null);
  // GEN-8: cached-but-not-live has to be visible; `error` alone hid every failure
  // that happened to land on top of a populated cache.
  const [stale, setStale] = useState<Staleness | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state (seeded from cache; secrets are never cached/pre-filled).
  const [hostingEnabled, setHostingEnabled] = useState(cachedSettings?.data.hosting_enabled ?? HOSTING_DEFAULTS.hosting_enabled);
  const [backend, setBackend] = useState(cachedSettings?.data.hosting_backend ?? HOSTING_DEFAULTS.hosting_backend);
  const [publicHost, setPublicHost] = useState(cachedSettings?.data.hosting_public_host ?? HOSTING_DEFAULTS.hosting_public_host);
  const [r2AccountId, setR2AccountId] = useState(cachedSettings?.data.r2_account_id ?? HOSTING_DEFAULTS.r2_account_id);
  const [r2AccessKeyId, setR2AccessKeyId] = useState("");
  const [r2SecretAccessKey, setR2SecretAccessKey] = useState("");
  const [r2Bucket, setR2Bucket] = useState(cachedSettings?.data.r2_bucket ?? HOSTING_DEFAULTS.r2_bucket);

  /** Seed the form from a server/cache response, filling the fresh-install gaps. */
  function seedForm(s: HostingSettings) {
    setHostingEnabled(s.hosting_enabled ?? HOSTING_DEFAULTS.hosting_enabled);
    setBackend(s.hosting_backend ?? HOSTING_DEFAULTS.hosting_backend);
    setPublicHost(s.hosting_public_host ?? HOSTING_DEFAULTS.hosting_public_host);
    setR2AccountId(s.r2_account_id ?? HOSTING_DEFAULTS.r2_account_id);
    setR2Bucket(s.r2_bucket ?? HOSTING_DEFAULTS.r2_bucket);
    // Never pre-fill secrets
    setR2AccessKeyId("");
    setR2SecretAccessKey("");
  }

  async function refresh() {
    const cached = readCacheEntry<HostingSettings>(HOSTING_CACHE);
    if (!cached) setLoading(true);  // only spin when there's nothing cached to show
    setError(null);
    const epoch = cacheEpoch();
    try {
      const s = await client.getHostingSettings();
      writeCache(HOSTING_CACHE, s, epoch);
      setSettings(s);
      seedForm(s);
      setStale(null);
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      if (!cached) setError(reason);
      else setStale({ cachedAt: cached.at, reason });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isSuper) {
      setLoading(false);
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, isSuper]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const input: HostingSettingsInput = {
        hosting_enabled: hostingEnabled,
        hosting_backend: backend,
        hosting_public_host: publicHost,
        r2_account_id: r2AccountId,
        r2_bucket: r2Bucket,
      };
      // Only send R2 secrets if the user entered new values
      if (r2AccessKeyId) input.r2_access_key_id = r2AccessKeyId;
      if (r2SecretAccessKey) input.r2_secret_access_key = r2SecretAccessKey;
      const epoch = cacheEpoch();
      const next = await client.setHostingSettings(input);
      writeCache(HOSTING_CACHE, next, epoch);
      setSettings(next);
      seedForm(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  /**
   * r2 + empty public host makes `static_url` return ROOT-RELATIVE refs: uploads
   * succeed, but every persisted asset URL then resolves against whatever origin
   * opened it → cross-device 404s. The cloud 422s the combination, but the operator
   * should learn it while typing, not from a failed Save.
   */
  const r2NeedsPublicHost = backend === "r2" && publicHost.trim() === "";

  if (!isSuper) {
    return (
      <section className="admin-tab" data-testid="admin-hosting">
        <EmptyState
          icon="lock"
          title="Requires super admin"
          hint="Hosting configuration is platform-global."
          testId="hosting-requires-super"
        />
      </section>
    );
  }

  return (
    <section className="admin-tab" data-testid="admin-hosting">
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          <strong className="admin-panel__title panel-title">Hosting &amp; CDN</strong>
          <p className="admin-panel__sub">Configure the hosting backend and CDN settings for image delivery.</p>
        </div>
      </div>

      {error && <p role="alert">{error}</p>}
      <StaleNotice stale={stale} testid="hosting-stale" />

      {loading ? (
        <p className="faint" data-testid="hosting-loading">Loading hosting settings...</p>
      ) : (
        <>
          <div className="admin-tab__form admin-tab__form--stacked">
            {/* Hosting enabled toggle */}
            <div className="field">
              <label className="field__check-label" htmlFor="hosting-enabled">Hosting enabled</label>
              <div className="row field--inline-check">
                <input
                  id="hosting-enabled"
                  type="checkbox"
                  checked={hostingEnabled}
                  onChange={(e) => setHostingEnabled(e.target.checked)}
                  data-testid="hosting-enabled"
                />
                <span className="faint">{hostingEnabled ? "Enabled" : "Disabled"}</span>
              </div>
            </div>

            {/* Backend selector */}
            <div className="field field--narrow">
              <label htmlFor="hosting-backend">Backend</label>
              <select
                id="hosting-backend"
                value={backend}
                onChange={(e) => setBackend(e.target.value)}
                data-testid="hosting-backend"
              >
                <option value="local">local</option>
                <option value="r2">r2</option>
              </select>
            </div>

            {/* Public host URL */}
            <div className="field field--wide">
              <label htmlFor="hosting-public-host">Public host URL</label>
              <input
                id="hosting-public-host"
                type="text"
                value={publicHost}
                onChange={(e) => setPublicHost(e.target.value)}
                placeholder="https://cdn.example.com"
                aria-invalid={r2NeedsPublicHost}
                data-testid="hosting-public-host"
              />
              {r2NeedsPublicHost && (
                <p role="alert" className="faint" data-testid="hosting-public-host-warning">
                  A public host URL is required with the r2 backend — without it every stored
                  asset URL is root-relative and won’t resolve on other devices.
                </p>
              )}
            </div>

            {/* R2 credentials (only when backend = r2) */}
            {backend === "r2" && (
              <>
                <p className="label admin-tab__form-section">Cloudflare R2 credentials</p>
                {settings?.r2_configured ? (
                  <span className="badge badge--success" data-testid="r2-status">R2 Configured</span>
                ) : (
                  <span className="badge badge--neutral" data-testid="r2-status">R2 Not configured</span>
                )}

                <div className="field field--medium">
                  <label htmlFor="r2-account-id">Account ID</label>
                  <input
                    id="r2-account-id"
                    type="text"
                    value={r2AccountId}
                    onChange={(e) => setR2AccountId(e.target.value)}
                    placeholder="Cloudflare account ID"
                    data-testid="r2-account-id"
                  />
                </div>

                <div className="field field--medium">
                  <label htmlFor="r2-access-key-id">Access Key ID</label>
                  <input
                    id="r2-access-key-id"
                    type="text"
                    value={r2AccessKeyId}
                    onChange={(e) => setR2AccessKeyId(e.target.value)}
                    placeholder="Enter access key ID..."
                    data-testid="r2-access-key-id"
                  />
                </div>

                <div className="field field--medium">
                  <label htmlFor="r2-secret-access-key">Secret Access Key</label>
                  <input
                    id="r2-secret-access-key"
                    type="password"
                    value={r2SecretAccessKey}
                    onChange={(e) => setR2SecretAccessKey(e.target.value)}
                    placeholder="Enter secret access key..."
                    data-testid="r2-secret-access-key"
                  />
                </div>

                <div className="field field--medium">
                  <label htmlFor="r2-bucket">Bucket name</label>
                  <input
                    id="r2-bucket"
                    type="text"
                    value={r2Bucket}
                    onChange={(e) => setR2Bucket(e.target.value)}
                    placeholder="my-bucket"
                    data-testid="r2-bucket"
                  />
                </div>
              </>
            )}

          </div>
          {/* Rule 7: the commit is at the BOTTOM of the column, in the bar, and it is the one
              primary on this surface. It used to be a loose row inside the field stack. */}
          <ActionBar
            label="Hosting actions"
            primary={
              <button
                type="button"
                className="btn btn--primary"
                disabled={saving || r2NeedsPublicHost}
                onClick={handleSave}
                data-testid="hosting-save"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            }
          />
        </>
      )}
    </section>
  );
}
